"use client";

import { useDict } from "@/components/i18n/DictProvider";
import { Signal } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { DetailHeroBanner } from "@/components/modules/DetailHeroBanner";
import { type Lang } from "@/lib/i18n";
import { detailCrumbs } from "@/lib/routes/detailCrumbs";
import { moduleHero, moduleHeroAlt } from "@/lib/lecturehub/poster";
import type { Module } from "@/lib/lecturehub/types";

type Props = {
  lang: Lang;
  module: Module;
  /** Render the in-app (/library) breadcrumb trail instead of the public one. */
  library?: boolean;
  /** Guest/public surface: the marketing header overlays the banner, so add
   *  top clearance padding. False on the in-app (AppShell) surface. */
  overlayHeader?: boolean;
  /** Portal.cloudflareAccountHash — without it the hero falls back to the raw
   *  asset `uri` instead of the optimized CF `xl` variant. */
  cfHash?: string | null;
};

/**
 * Top header of a course detail page: a full-bleed hero banner (cover image ·
 * badges · title) with the breadcrumb in a light strip below it.
 */
export function CourseHero({
  lang,
  module: m,
  library = false,
  overlayHeader = false,
  cfHash = null,
}: Props) {
  const t = useDict();
  const level = m.course?.courseLevel;
  const levelLabel = level
    ? /^\d+$/.test(level)
      ? `${t.detail.levelPrefix} ${level}`
      : level
    : null;

  return (
    <>
      <DetailHeroBanner
        imageUrl={moduleHero(m, cfHash)}
        imageAlt={moduleHeroAlt(m, m.name)}
        overlayHeader={overlayHeader}
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Badge tone="gold">{t.detail.typeCourse}</Badge>
          {levelLabel ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/90">
              <Signal className="size-3" aria-hidden />
              {levelLabel}
            </span>
          ) : null}
        </div>
        <h1 className="max-w-4xl !text-[28px] !font-semibold !leading-[1.08] !tracking-tight !text-white md:!text-[44px]">
          {m.name}
        </h1>
      </DetailHeroBanner>
      <section>
        <Container className="pt-5">
          <Breadcrumb items={detailCrumbs(t, lang, "courses", m.name, library)} />
        </Container>
      </section>
    </>
  );
}
